/**
 * AG Grid User Settings Service
 * 
 * This service manages user-specific AG Grid settings such as column state and grid view preferences.
 * It's part of a larger AG Grid ecosystem that includes edit functionality, table communication, and more.
 * 
 * ## AG Grid Architecture Overview
 * 
 * ### Core Components:
 * 1. **ag-grid-table.component.ts** - Main table component
 * 2. **ag-grid-edit.service.ts** - Handles cell/row editing and submission
 * 3. **ag-grid-user-settings.service.ts** - Persists user preferences (this file)
 * 4. **table-communication.service.ts** - Inter-component communication
 * 5. **ag-grid-tools.service.ts** - Utility functions
 * 6. **ag-grid-filters.service.ts** - Filter management
 * 
 * ### Edit Functionality (Recently Refactored)
 * 
 * The edit system now uses **record IDs** instead of row indices for stability:
 * - **Change Tracking**: `Map<recordId, changedFields>` instead of `Map<rowIndex, changedFields>`
 * - **Why**: Row indices change when sorting/filtering, but record IDs are stable
 * - **Grid Config**: Uses `getRowId: (params) => params.data.id` to establish stable IDs
 * 
 * #### Edit Flow:
 * 1. User edits cell → `onCellValueChanged` fires
 * 2. Changes stored in `changedRows` Map keyed by record ID
 * 3. Original values stored in `originalData` Map with "recordId-field" keys
 * 4. On Save: `submitFullEdit()` calls `api.stopEditing()` then submits all changes
 * 5. Edit service uses `api.getRowNode(recordId)` to find rows regardless of sort/filter state
 * 
 * #### Key Features:
 * - **Full Edit Mode**: Edit all visible rows at once
 * - **Single Row Edit**: Edit one row at a time
 * - **Bulk Edit**: Edit multiple rows with same value
 * - **Revert Changes**: Restore original values before saving
 * 
 * ### User Settings (This Service)
 * 
 * Persists two types of settings:
 * 1. **Column State**: Column order, width, visibility, sort, filter
 * 2. **Grid View State**: Custom view preferences
 * 
 * Settings are namespaced by:
 * - Table entity type (e.g., 'items', 'challenges')
 * - Optional parent view name for embedded grids
 * - Prefixed with 'ag-grid-' for storage
 * 
 * ### Integration Points:
 * - Settings are loaded in `onGridReady()`
 * - Column changes trigger `debounceSaveColumnState()`
 * - User-specific settings via `dataService.updateUserSettings()`
 */

import { Injectable } from '@angular/core';
import { ColDef, ColGroupDef } from 'ag-grid-enterprise';
import { DataService } from 'src/app/services/data.service';
@Injectable({
  providedIn: 'root'
})
export class AgGridUserSettings {

  constructor
  (
    private dataService: DataService
  ) {}

  /**
   * Saves the current column state (order, width, visibility, sort) to user preferences
   * @param table - Entity type (e.g., 'items', 'challenges')
   * @param state - AG Grid column state array
   * @param isViewEmbedded - Whether the grid is embedded in another view
   * @param parentViewName - Parent view name for namespacing embedded grids
   */
  async saveColumnState(table: string, state: any, isViewEmbedded: boolean = false, parentViewName: any) {
    try {
      if(isViewEmbedded && parentViewName) {
        table = 'ag-grid-'+ `${parentViewName}-` + table;
      } else {
      table = 'ag-grid-'+table;
      }
      // Code to save the current column state to the user's profile
      let response: any = await this.dataService.updateUserSettings(table, 'columnState', state);
    } catch (error) {
      console.error("Failed to update user settings", error);
    }
  }

  /**
   * Retrieves saved column state from user preferences
   * @param table - Entity type (e.g., 'items', 'challenges')
   * @param isViewEmbedded - Whether the grid is embedded in another view
   * @param parentViewName - Parent view name for namespacing embedded grids
   * @returns Saved column state array or undefined
   */
  async getColumnState(table: string, isViewEmbedded: boolean = false, parentViewName: any) {
    try {
      if(isViewEmbedded && parentViewName) {
        table = 'ag-grid-'+ `${parentViewName}-` + table;
      } else {
      table = 'ag-grid-'+table;
      }
      // Code to retrieve the current column state from the user's profile
      return await this.dataService.getUserSettings(table, 'columnState');
    } catch (error) {
      console.error("Failed to retrieve user settings", error);
    }
  }

  /**
   * Saves custom grid view preferences to user settings
   * @param table - Entity type (e.g., 'items', 'challenges')
   * @param state - Custom grid view state object
   */
  async saveGridViewState(table: string, state: any) {
    try {
      table = 'ag-grid-'+table;
      // Code to save the current grid view state to the user's profile
      let response: any = await this.dataService.updateUserSettings(table, 'gridViewState', state);
    } catch (error) {
      console.error("Failed to update user settings", error);
    }
  }

  /**
   * Retrieves saved grid view preferences from user settings
   * @param table - Entity type (e.g., 'items', 'challenges')
   * @returns Saved grid view state or undefined
   */
  async getGridViewState(table: string) {
    try {
      table = 'ag-grid-'+table;
      // Code to retrieve the current grid view state from the user's profile
      return await this.dataService.getUserSettings(table, 'gridViewState');
    } catch (error) {
      console.error("Failed to retrieve user settings", error);
    }
  }
}
